import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CountryModel,
    CustomerValidationDto,
    StateModel,
} from "@insite/client-framework/Types/ApiModels";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { FormFieldStyle } from "@insite/mobius/FormField";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Radio, { RadioComponentProps, RadioStyle } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    firstName: string;
    lastName: string;
    attention: string;
    companyName: string;
    address1: string;
    address2: string;
    address3: string;
    address4: string;
    country: BaseAddressModel["country"];
    state: BaseAddressModel["state"];
    city: string;
    postalCode: string;
    phone: string;
    email: string;
    fax: string;
    onChangeFirstName: TextFieldProps["onChange"];
    onChangeLastName: TextFieldProps["onChange"];
    onChangeAttention: TextFieldProps["onChange"];
    onChangeCompanyName: TextFieldProps["onChange"];
    onChangeAddress1: TextFieldProps["onChange"];
    onChangeAddress2: TextFieldProps["onChange"];
    onChangeAddress3: TextFieldProps["onChange"];
    onChangeAddress4: TextFieldProps["onChange"];
    onChangeCountry: (event: React.ChangeEvent<HTMLSelectElement>, country: BaseAddressModel["country"]) => void;
    onChangeState: (event: React.ChangeEvent<HTMLSelectElement>, state: BaseAddressModel["state"]) => void;
    onChangeCity: TextFieldProps["onChange"];
    onChangePostalCode: TextFieldProps["onChange"];
    onChangePhone: TextFieldProps["onChange"];
    onChangeEmail: TextFieldProps["onChange"];
    onChangeFax: TextFieldProps["onChange"];
    firstNameError: React.ReactNode;
    lastNameError: React.ReactNode;
    attentionError: React.ReactNode;
    companyNameError: React.ReactNode;
    address1Error: React.ReactNode;
    address2Error: React.ReactNode;
    address3Error: React.ReactNode;
    address4Error: React.ReactNode;
    countryError: React.ReactNode;
    stateError: React.ReactNode;
    cityError: React.ReactNode;
    postalCodeError: React.ReactNode;
    phoneError: React.ReactNode;
    emailError: React.ReactNode;
    faxError: React.ReactNode;
    countries: CountryModel[];
    fieldDisplay: AddressFieldDisplayCollectionModel;
    validation: CustomerValidationDto;
    extendedStyles?: CustomerAddressFormFieldStyles;
    nickname: string | undefined;
    onChangeNickName: TextFieldProps["onChange"];
    nickNameError: React.ReactNode;
    postalcode2: string;
    removeinfuture: string;
    onChangePostalCode2: TextFieldProps["onChange"];
    onChangeRemoveInFuture: (value: string) => void;
    showRemoveInFuture: boolean | undefined;
}

type Props = OwnProps;

export interface CustomerAddressFormFieldStyles {
    form?: InjectableCss;
    container?: GridContainerProps;
    radioButton?: RadioComponentProps;
    radioGroupDiv?: RadioGroupProps;
    postalCode2GridItem?: GridItemProps;
    removeInFutureGridItem?: GridItemProps;
    postalCode2Text?: TextFieldPresentationProps;
    nickNameGridItem?: GridItemProps;
    nickNameText?: TextFieldPresentationProps;
    removeInFutureText?: TypographyProps;
    firstNameGridItem?: GridItemProps;
    firstNameText?: TextFieldPresentationProps;
    lastNameGridItem?: GridItemProps;
    lastNameText?: TextFieldPresentationProps;
    companyNameGridItem?: GridItemProps;
    companyNameText?: TextFieldPresentationProps;
    attentionGridItem?: GridItemProps;
    attentionText?: TextFieldPresentationProps;
    address1GridItem?: GridItemProps;
    address1Text?: TextFieldPresentationProps;
    address2GridItem?: GridItemProps;
    address2Text?: TextFieldPresentationProps;
    address3GridItem?: GridItemProps;
    address3Text?: TextFieldPresentationProps;
    address4GridItem?: GridItemProps;
    address4Text?: TextFieldPresentationProps;
    countryGridItem?: GridItemProps;
    countrySelect?: SelectPresentationProps;
    cityGridItem?: GridItemProps;
    cityText?: TextFieldPresentationProps;
    stateGridItem?: GridItemProps;
    stateSelect?: SelectPresentationProps;
    postalCodeGridItem?: GridItemProps;
    postalCodeText?: TextFieldPresentationProps;
    phoneGridItem?: GridItemProps;
    phoneText?: TextFieldPresentationProps;
    faxGridItem?: GridItemProps;
    faxText?: TextFieldPresentationProps;
    emailGridItem?: GridItemProps;
    emailText?: TextFieldPresentationProps;
    formButtonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const customerAddressFormFieldStyles: CustomerAddressFormFieldStyles = {
    container: {
        css: css`
            > ${GridOffset} {
                > ${GridItemStyle} {
                    padding-bottom: 5px;
                    padding-top: 13px;
                }
            }
            ${({ theme }) => { return breakpointMediaQueries(
                theme, [null, css` max-width: 100%; `,],"max",); 
            }}
        `,
    },
    // This style property is being used by P21 ---- P21 Change
    companyNameText:{
        css: css`
        border: 1px solid red
        `
    },
    radioGroupDiv: {
        css: css`
            flex-direction: row;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-direction: column;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
            > ${RadioStyle} {
                > input {
                    margin: 0px 1px;
                }
                > label {
                    font-size: 16px;
                }
            }
            > ${RadioStyle} + ${RadioStyle} {
                margin-top: 0px;
                margin-left: 16px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                margin-left: 0px;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }
        `,
    },
    nickNameGridItem: { width: 12 },
    postalCode2GridItem: {
        width: [4, 2, 6, 6, 6],
        css: css`
            > ${FormFieldStyle} {
                label {
                    visibility: hidden;
                    max-height: 22px;
                }
            }
            ${({ theme }) => { return breakpointMediaQueries(
                theme, [null, css` padding-left: 0; `,],"max",); 
            }}
        `,
    },
    removeInFutureGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    removeInFutureText: {
        css: css`
            font-size: 14px;
            text-transform: capitalize;
            font-weight: 700;
        `,
    },
    firstNameGridItem: { width: [12, 12, 6, 6, 6] },
    lastNameGridItem: { width: [12, 12, 6, 6, 6] },
    companyNameGridItem: { width: 12 },
    attentionGridItem: { width: 6 },
    address1GridItem: { width: 12 },
    address2GridItem: { width: 12 },
    address3GridItem: { width: 12 },
    address4GridItem: { width: 12 },
    countryGridItem: { width: 12 },
    cityGridItem: { width: [12, 12, 6, 6, 6] },
    stateGridItem: { width: [12, 12, 6, 6, 6] },
    postalCodeGridItem: { width: [8, 10, 6, 6, 6] },
    phoneGridItem: { width: 6 },
    faxGridItem: { width: 6 },
    emailGridItem: { width: 12 },
    formButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 30px;
        `,
    },
    cancelButton: {
        color: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
};

const CustomerAddressFormFields = ({
    firstName,
    lastName,
    attention,
    companyName,
    address1,
    address2,
    address3,
    address4,
    country,
    state,
    city,
    postalCode,
    phone,
    email,
    fax,
    onChangeFirstName,
    onChangeLastName,
    onChangeAttention,
    onChangeCompanyName,
    onChangeAddress1,
    onChangeAddress2,
    onChangeAddress3,
    onChangeAddress4,
    onChangeCountry,
    onChangeState,
    onChangeCity,
    onChangePostalCode,
    onChangePhone,
    onChangeEmail,
    onChangeFax,
    firstNameError,
    lastNameError,
    companyNameError,
    address1Error,
    countryError,
    stateError,
    cityError,
    postalCodeError,
    countries,
    fieldDisplay,
    validation,
    extendedStyles,
    nickname,
    onChangeNickName,
    postalcode2,
    removeinfuture,
    onChangeRemoveInFuture,
    onChangePostalCode2,
    nickNameError,
    showRemoveInFuture,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(customerAddressFormFieldStyles, extendedStyles));
    if (!countries) {
        return null;
    }

    const [selectedCountry, setSelectedCountry] = useState<CountryModel | undefined>(undefined);
    const [states, setStates] = useState<StateModel[]>([]);

    useEffect(() => {
        setSelectedCountry(countries.find(c => c.id === country?.id));
    }, [country]);

    useEffect(() => {
        setStates(selectedCountry?.states || []);
    }, [selectedCountry]);

    const handleCountryChange: SelectProps["onChange"] = event => {
        const country = countries.find(c => c.id === event.target.value);
        setSelectedCountry(country);
        onChangeCountry(event, country || null);
    };

    const handleStateChange: SelectProps["onChange"] = event => {
        const state = selectedCountry?.states?.find(s => s.id === event.target.value);
        onChangeState(event, state || null);
    };

    const generateGridItemStyle = (errorCondition: any) => css`
        > ${FormFieldStyle} {
            label {
                color: ${errorCondition ? "#ac1e2d" : ""};
            }
        }
    `;

    const isPRRegion = typeof window !== "undefined" && window.location.hostname.endsWith('.pr');
    

    return (
        <GridContainer {...styles.container} data-test-selector="customerAddressFormFields">
            <GridItem {...styles.nickNameGridItem} css={generateGridItemStyle(nickNameError)}>
                <TextField
                    {...styles.nickNameText}
                    name="nickname"
                    label={"Nickname *"}
                    value={nickname}
                    onChange={onChangeNickName}
                    data-test-selector="customerAddressForm_nickName"
                />
            </GridItem>
            {fieldDisplay.companyName!.isVisible && (
                <GridItem {...styles.companyNameGridItem} css={generateGridItemStyle(companyNameError)}>
                    <TextField
                                   {...styles.companyNameText}
                                   name="companyName"
                                   label={"Company"}
                                   value={companyName}
                                   disabled={validation.companyName!.isDisabled}
                                   required={validation.companyName!.isRequired}
                                   maxLength={50}
                                   onChange={onChangeCompanyName}
                                   data-test-selector="customerAddressForm_companyName"
                    />
                </GridItem>
            )}
 
            {fieldDisplay.firstName!.isVisible && (
                <GridItem {...styles.firstNameGridItem} css={generateGridItemStyle(firstNameError)}>
                    <TextField
                        {...styles.firstNameText}
                        name="firstName"
                        label={fieldDisplay.firstName!.displayName}
                        value={firstName}
                        disabled={validation.firstName!.isDisabled}
                        required={validation.firstName!.isRequired}
                        maxLength={validation.firstName!.maxLength || undefined}
                        onChange={onChangeFirstName}
                        data-test-selector="customerAddressForm_firstName"
                    />
                </GridItem>
            )}
            {fieldDisplay.lastName!.isVisible && (
                <GridItem {...styles.lastNameGridItem} css={generateGridItemStyle(lastNameError)}>
                    <TextField
                        {...styles.lastNameText}
                        name="lastName"
                        label={fieldDisplay.lastName!.displayName}
                        value={lastName}
                        disabled={validation.lastName!.isDisabled}
                        required={validation.lastName!.isRequired}
                        maxLength={validation.lastName!.maxLength || undefined}
                        onChange={onChangeLastName}
                        data-test-selector="customerAddressForm_lastName"
                    />
                </GridItem>
            )}

            { !isPRRegion && fieldDisplay.attention!.isVisible && (
                <GridItem {...styles.attentionGridItem}>
                    <TextField
                        {...styles.attentionText}
                        name="attention"
                        label={fieldDisplay.attention!.displayName}
                        value={attention}
                        disabled={validation.attention!.isDisabled}
                        required={validation.attention!.isRequired}
                        maxLength={validation.attention!.maxLength || undefined}
                        onChange={onChangeAttention}
                        data-test-selector="customerAddressForm_attention"
                    />
                </GridItem>
            )}
            {fieldDisplay.address1!.isVisible && (
                <GridItem {...styles.address1GridItem} css={generateGridItemStyle(address1Error)}>
                    <TextField
                        {...styles.address1Text}
                        name="address1"
                        label={"Address line 1"}
                        value={address1}
                        disabled={validation.address1!.isDisabled}
                        required={validation.address1!.isRequired}
                        maxLength={50}
                        onChange={onChangeAddress1}
                        data-test-selector="customerAddressForm_address1"
                    />
                </GridItem>
            )}
            {fieldDisplay.address2!.isVisible && (
                <GridItem {...styles.address2GridItem}>
                    <TextField
                        {...styles.address2Text}
                        name="address2"
                        label={"Address Line 2"}
                        value={address2}
                        disabled={validation.address2!.isDisabled}
                        required={validation.address2!.isRequired}
                        maxLength={50}
                        onChange={onChangeAddress2}
                        data-test-selector="customerAddressForm_address2"
                    />
                </GridItem>
            )}
            { !isPRRegion && fieldDisplay.address3!.isVisible && (
                <GridItem {...styles.address3GridItem}>
                    <TextField
                        {...styles.address3Text}
                        name="address3"
                        label={fieldDisplay.address3!.displayName}
                        value={address3}
                        disabled={validation.address3!.isDisabled}
                        required={validation.address3!.isRequired}
                        maxLength={validation.address3!.maxLength || undefined}
                        onChange={onChangeAddress3}
                        data-test-selector="customerAddressForm_address3"
                    />
                </GridItem>
            )}
            { !isPRRegion && fieldDisplay.address4!.isVisible && (
                <GridItem {...styles.address4GridItem}>
                    <TextField
                        {...styles.address4Text}
                        name="address4"
                        label={fieldDisplay.address4!.displayName}
                        value={address4}
                        disabled={validation.address4!.isDisabled}
                        required={validation.address4!.isRequired}
                        maxLength={validation.address4!.maxLength || undefined}
                        onChange={onChangeAddress4}
                        data-test-selector="customerAddressForm_address4"
                    />
                </GridItem>
            )}
            {fieldDisplay.country!.isVisible && countries.length > 1 && (
                <GridItem {...styles.countryGridItem}>
                    <Select
                        {...styles.countrySelect}
                        label={fieldDisplay.country!.displayName}
                        value={selectedCountry?.id || ""}
                        disabled={validation.country!.isDisabled || countries.length === 1}
                        required={validation.country!.isRequired}
                        onChange={handleCountryChange}
                        error={countryError}
                        data-test-selector="customerAddressForm_country"
                    >
                        <option value="">{translate("Select Country")}</option>
                        {countries.map(co => (
                            <option key={co.id} value={co.id}>
                                {co.name}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}
            {fieldDisplay.city!.isVisible && (
                <GridItem {...styles.cityGridItem} css={generateGridItemStyle(cityError)}>
                    <TextField
                        {...styles.cityText}
                        name="city"
                        label={fieldDisplay.city!.displayName}
                        value={city}
                        disabled={validation.city!.isDisabled}
                        required={validation.city!.isRequired}
                        maxLength={50}
                        onChange={onChangeCity}
                        data-test-selector="customerAddressForm_city"
                    />
                </GridItem>
            )}
            {states.length > 0 && (
                <GridItem {...styles.stateGridItem} css={generateGridItemStyle(stateError)}>
                    <Select
                        {...styles.stateSelect}
                        label={fieldDisplay.state!.displayName}
                        value={state?.id || ""}
                        disabled={validation.state!.isDisabled}
                        required
                        onChange={handleStateChange}
                        data-test-selector="customerAddressForm_state"
                    >
                        <option value="">{translate("Select State")}</option>
                        {states.map(st => (
                            <option key={st.id} value={st.id}>
                                {st.name}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}
            {fieldDisplay.postalCode!.isVisible && (
                <GridItem {...styles.postalCodeGridItem} css={generateGridItemStyle(postalCodeError)}>
                    <TextField
                        {...styles.postalCodeText}
                        name="postalCode"
                        label={"Zip code"}
                        value={postalCode}
                        disabled={validation.postalCode!.isDisabled}
                        required={validation.postalCode!.isRequired}
                        maxLength={validation.postalCode!.maxLength || undefined}
                        onChange={onChangePostalCode}
                        data-test-selector="customerAddressForm_postalCode"
                    />
                </GridItem>
            )}
            <GridItem {...styles.postalCode2GridItem}>
                <TextField
                    {...styles.postalCode2Text}
                    name="postalcode2"
                    label={"Postal Code 2"}
                    value={postalcode2}
                    onChange={onChangePostalCode2}
                    data-test-selector="customerAddressForm_postalcode2"
                />
            </GridItem>
            {showRemoveInFuture && (
                <GridItem {...styles.removeInFutureGridItem}>
                    <Typography {...styles.removeInFutureText}>{translate("Select One *")}</Typography>
                    <RadioGroup
                        {...styles.radioGroupDiv}
                        value={removeinfuture}
                        onChangeHandler={event => onChangeRemoveInFuture(event.target.value)}
                    >
                        <Radio {...styles.radioButton} key="saveOption" value="saveOption">
                            {translate("Save For Future Use")}
                        </Radio>
                        <Radio {...styles.radioButton} key="doNotSaveOption" value="doNotSaveOption">
                            {translate("Do Not Save")}
                        </Radio>
                    </RadioGroup>
                </GridItem>
            )}
            { !isPRRegion && fieldDisplay.phone?.isVisible && (
                <GridItem {...styles.phoneGridItem}>
                    <TextField
                        {...styles.phoneText}
                        name="phone"
                        label={fieldDisplay.phone!.displayName}
                        value={phone}
                        disabled={validation.phone!.isDisabled}
                        required={validation.phone!.isRequired}
                        maxLength={validation.phone!.maxLength || undefined}
                        onChange={onChangePhone}
                        data-test-selector="customerAddressForm_phone"
                    />
                </GridItem>
            )}
            {!isPRRegion && fieldDisplay.fax?.isVisible && (
                <GridItem {...styles.faxGridItem}>
                    <TextField
                        {...styles.faxText}
                        name="fax"
                        label={fieldDisplay.fax!.displayName}
                        value={fax}
                        disabled={validation.fax!.isDisabled}
                        required={validation.fax!.isRequired}
                        maxLength={validation.fax!.maxLength || undefined}
                        onChange={onChangeFax}
                        data-test-selector="customerAddressForm_fax"
                    />
                </GridItem>
            )}
            { !isPRRegion && fieldDisplay.email?.isVisible && (
                <GridItem {...styles.emailGridItem}>
                    <TextField
                        {...styles.emailText}
                        name="email"
                        label={fieldDisplay.email!.displayName}
                        value={email}
                        disabled={validation.email!.isDisabled}
                        required={validation.email!.isRequired}
                        maxLength={validation.email!.maxLength || undefined}
                        onChange={onChangeEmail}
                        data-test-selector="customerAddressForm_email"
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default CustomerAddressFormFields;
